import React, { useEffect, useState } from 'react';
import { Task, TaskStage } from '../../../../types/Task';
import { taskService } from '../../../../services/quantifiedTaskService';
import { useAuth } from '../../../../contexts/AuthContext';
import { Button, Alert, Form } from '@/components/ui';
import ResponsiveModal from '../../../../components/ResponsiveModal';
import { getEffortCount } from '../../utils';
import { describeActionError } from '../../../../utils/loadError';

const COMMENTS_FIELD_ID = 'close-task-comments';
const COMMENTS_REQUIRED = 'Comments required when no efforts are recorded';
const CLOSE_FAILED = "Couldn't close the task. Try again.";

interface CloseTaskModalProps {
  visible: boolean;
  onClose: () => void;
  task: Task;
  onSubmit: (comments: string) => Promise<void>;
}

const CloseTaskModal: React.FC<CloseTaskModalProps> = ({
  visible,
  onClose,
  task,
  onSubmit,
}) => {
  const [comments, setComments] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showZeroValueWarning, setShowZeroValueWarning] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (visible) {
      setCommentsError(null);
      setSubmitError(null);
    }
  }, [visible]);

  // FORM-01: the message goes under the field, and focus moves to it.
  const requireComments = (): boolean => {
    if (hasNoEfforts() && !comments.trim()) {
      setShowZeroValueWarning(false);
      setCommentsError(COMMENTS_REQUIRED);
      setTimeout(() => document.getElementById(COMMENTS_FIELD_ID)?.focus(), 0);
      return false;
    }
    return true;
  };

  // Check if actual total value is $0
  const hasZeroActualValue = () => {
    if (!task.taskCostings || task.taskCostings.length === 0) {
      return false;
    }
    
    // Check if all task costings have actualTotalValue of 0 or undefined
    return task.taskCostings.every(costing => 
      costing.actualTotalValue === 0 || costing.actualTotalValue === undefined || costing.actualTotalValue === null
    );
  };

  // Check if task has any efforts.
  //
  // getEffortCount, not `!task.efforts`: this modal is opened with a row straight from the task
  // list, and list rows no longer carry effort arrays - only the server's effortSummary. Testing
  // the array directly would report "no efforts" for every task and make comments mandatory
  // even where effort exists.
  const hasNoEfforts = () => getEffortCount(task) === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if task has no efforts and no comments - require comments
    if (!requireComments()) return;
    
    // Check if actual total value is $0 and show warning
    if (hasZeroActualValue() && !showZeroValueWarning) {
      setShowZeroValueWarning(true);
      return;
    }
    
    try {
      setLoading(true);
      setSubmitError(null);
      await onSubmit(comments);
      onClose();
    } catch (error) {
      console.error('Error closing task:', error);
      setSubmitError(describeActionError(error, CLOSE_FAILED));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmClose = async () => {
    // Check if task has no efforts and no comments - require comments
    if (!requireComments()) return;
    
    try {
      setLoading(true);
      setSubmitError(null);
      await onSubmit(comments);
      onClose();
    } catch (error) {
      console.error('Error closing task:', error);
      setSubmitError(describeActionError(error, CLOSE_FAILED));
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <form onSubmit={handleSubmit}>
      {submitError && (
        <Alert variant="danger" role="alert">
          {submitError}
        </Alert>
      )}
      {showZeroValueWarning ? (
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
          <strong>Warning:</strong> The actual total value for this task is $0. Are you sure you want to close this task without any actual accomplishment value?
        </Alert>
      ) : (
        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle tw-mr-2"></i>
          Are you sure you want to close this task? This action will lock the task from further modifications.
        </Alert>
      )}

      {hasNoEfforts() && (
        <Alert variant="info">
          <i className="bi bi-info-circle tw-mr-2"></i>
          <strong>Notice:</strong> This task has no efforts recorded. Completion comments are required to explain why no efforts were recorded.
        </Alert>
      )}

      <Form.Group className="tw-mb-3">
        <Form.Label>
          Completion Comments
          {hasNoEfforts() && <span className="tw-text-danger tw-ml-1">*</span>}
        </Form.Label>
        <Form.Control
          id={COMMENTS_FIELD_ID}
          as="textarea"
          rows={4}
          value={comments}
          onChange={(e) => {
            setComments(e.target.value);
            if (commentsError) setCommentsError(null);
          }}
          placeholder={hasNoEfforts() ? "Comments are required when no efforts are recorded. Please explain why..." : "Add any completion comments..."}
          isInvalid={!!commentsError}
          aria-invalid={!!commentsError}
          aria-describedby={commentsError ? `${COMMENTS_FIELD_ID}-error` : undefined}
        />
        {commentsError && (
          <Form.Control.Feedback type="invalid" id={`${COMMENTS_FIELD_ID}-error`}>
            {commentsError}
          </Form.Control.Feedback>
        )}
        {hasNoEfforts() && (
          <div className="tw-mt-1 tw-text-sm tw-text-content-muted">
            <small>Comments are mandatory when closing a task without any efforts.</small>
          </div>
        )}
      </Form.Group>
    </form>
  );

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {showZeroValueWarning ? (
        <>
          <Button
            variant="outline-secondary"
            className="tw-mr-2"
            onClick={() => setShowZeroValueWarning(false)}
          >
            Go Back
          </Button>
          <Button
            variant="danger"
            disabled={loading}
            onClick={handleConfirmClose}
          >
            {loading ? 'Closing...' : 'Close Task Anyway'}
          </Button>
        </>
      ) : (
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          onClick={handleSubmit}
        >
          {loading ? 'Closing...' : 'Close Task'}
        </Button>
      )}
    </>
  );

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Close Task"
      footer={modalFooter}
    >
      {modalContent}
    </ResponsiveModal>
  );
};

export default CloseTaskModal; 